/**
 * useUserProfile Hook
 *
 * React Query hook for fetching and updating user profile data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getUserProfile, updateUserProfile, UserProfile } from '@/services/userProfile.service';
import { toast } from 'sonner';

// =============================================================================
// Query Keys
// =============================================================================

export const userProfileKeys = {
  all: ['userProfile'] as const,
  detail: (userId: string) => [...userProfileKeys.all, userId] as const,
};

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to get the current user's profile
 */
export function useCurrentUserProfile() {
  const { user } = useAuth();
  const userId = user?.uid;

  return useQuery({
    queryKey: userProfileKeys.detail(userId || ''),
    queryFn: () => getUserProfile(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update the current user's profile
 */
export function useUpdateUserProfile() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.uid;

  return useMutation({
    mutationFn: (updates: Partial<Omit<UserProfile, 'updatedAt'>>) => {
      if (!userId) throw new Error('User not authenticated');
      return updateUserProfile(userId, updates);
    },
    onSuccess: () => {
      // Invalidate the profile query to refetch
      if (userId) {
        void void queryClient.invalidateQueries({
          queryKey: userProfileKeys.detail(userId),
        });
      }
      toast.success(t('toasts.profile.saved'));
    },
    onError: () => {
      toast.error(t('toasts.profile.saveFailed'));
    },
  });
}

/**
 * Hook to get a specific user's profile (for admin use)
 */
export function useUserProfile(userId: string | undefined) {
  return useQuery({
    queryKey: userProfileKeys.detail(userId || ''),
    queryFn: () => getUserProfile(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
